import type { ContactHandleSchema } from '@/services/types/coverage-types';
import type { LoRScalarChoice } from '@/services/types/letters-types';

/**
 * Contact channels extracted from adjuster/carrier handles.
 */
export interface ContactChannels {
  email: string;
  phone: string;
  fax: string;
}

/**
 * Extract the first email, phone, and fax from a list of contact handles.
 *
 * This is the single shared utility for handle extraction — all LOR consumers
 * (useClaimFilingState, DraftLorPage, DraftClaimLorStep, ClaimLorStep) should
 * use this instead of duplicating the extraction loop.
 */
export function extractContactChannels(
  handles: ContactHandleSchema[] | null | undefined,
): ContactChannels {
  const result: ContactChannels = { email: '', phone: '', fax: '' };
  if (!handles) return result;
  handles.forEach((h) => {
    const t = String(h.handle_type || '').toUpperCase();
    const v = String(h.value || '');
    if (!result.email && t.includes('EMAIL') && v) result.email = v;
    if (!result.phone && t.includes('PHONE') && v) result.phone = v;
    if (!result.fax && t.includes('FAX') && v) result.fax = v;
  });
  return result;
}

/**
 * Build the preferred contact choice for an adjuster (EMAIL > FAX priority).
 *
 * Returns null if no EMAIL or FAX handle is found.
 */
export function buildPreferredContactChoice(
  handles: ContactHandleSchema[] | null | undefined,
  coverageId: string,
): LoRScalarChoice | null {
  if (!handles) return null;
  const preferredHandle =
    handles.find((h) =>
      String(h.handle_type || '').toUpperCase().includes('EMAIL'),
    ) ??
    handles.find((h) =>
      String(h.handle_type || '').toUpperCase().includes('FAX'),
    );
  if (!preferredHandle) return null;
  return {
    value: String(preferredHandle.value || ''),
    context: { coverage_id: coverageId, contact_handle: preferredHandle },
  };
}
